import { NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, effect, inject, input } from '@angular/core';
import { MatCardTitle } from '@angular/material/card';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatSlideToggle, MatSlideToggleChange } from '@angular/material/slide-toggle';
import { openChangePasswordDialog, TotpAuthComponent } from '@auth';
import { APPLICATION_TYPE, type Country, COUNTRY_LIST, DEFAULT_COUNTRY, FeatureFlagPipe, PhoneNumberClass } from '@core';
import { type ChildClient, type UserModel, userRoles, UserRolesTypes } from '@models';
import { Store } from '@ngrx/store';
import { AuthActions } from '@ngrx-store';
import { CountrySelectComponent, InlineInputComponent } from '@ui';

@Component({
  imports: [
    MatCardTitle,
    InlineInputComponent,
    CountrySelectComponent,
    NgClass,
    MatSlideToggle,
    NgTemplateOutlet,
    MatCheckbox,
    TotpAuthComponent,
    FeatureFlagPipe,
],
  selector: 'avs-personal-profile',
  templateUrl: './personal-profile.component.html',
})
export class PersonalProfileComponent extends PhoneNumberClass {
  user = input.required<UserModel>();

  client = input.required<ChildClient | undefined>();

  userRoles = userRoles;
  userRolesTypes = UserRolesTypes;

  protected readonly DEFAULT_COUNTRY = DEFAULT_COUNTRY;
  protected isMobile = inject(APPLICATION_TYPE) === 'mobile';

  private store = inject(Store);
  private dialog = inject(MatDialog);

  constructor() {
    super();

    effect(() => this.user() && this.setMaskByCountryCode(this.user().phone.countryCode));

    this.store.dispatch(AuthActions.refreshProfile());
  }

  changePassword() {
    openChangePasswordDialog(this.dialog);
  }

  toggleNotification(event: MatSlideToggleChange, type: 'sms' | 'email', user: UserModel) {
    const notificationsPreferences = {
      emailEnabled: type === 'email' ? event.checked : user.notificationsPreferences.emailEnabled,
      smsEnabled: type === 'sms' ? event.checked : user.notificationsPreferences.smsEnabled,
    };
    const clientId = this.getUserClient(user) || '';

    if (clientId) {
      this.store.dispatch(
        AuthActions.updateNotificationData({
          data: { notificationsPreferences },
          userId: user.id,
        }),
      );
    }
  }

  updateUserData(data: Record<string, string | object>, user: UserModel) {
    const clientId = this.getUserClient(user);

    if (clientId) {
      this.store.dispatch(AuthActions.updatePersonalData({ data }));
    }
  }

  updateCountryCode(country: Country, user: UserModel) {
    this.phoneMask.set(country.mask);
    this.phoneMaskExpression.set(country.maskExpression);
    this.store.dispatch(
      AuthActions.updatePersonalData({
        data: {
          phone: {
            countryCode: country.code,
            countryNumberCode: country.phoneCode,
            number: user.phone.number,
            smsOptIn: user.phone.smsOptIn,
          },
        },
      }),
    );
  }

  updatePhoneNumber($event: Record<string, string>, user: UserModel) {
    this.store.dispatch(
      AuthActions.updatePersonalData({
        data: {
          phone: {
            countryCode: user.phone.countryCode,
            countryNumberCode:
              COUNTRY_LIST.find(country => country.code === user.phone.countryCode)?.phoneCode ||
              DEFAULT_COUNTRY.phoneCode,
            number: Object.values($event)[0],
            smsOptIn: user.phone.smsOptIn,
          },
        },
      }),
    );
  }

  updateSMSOptIn(value: boolean, user: UserModel) {
    this.store.dispatch(
      AuthActions.updatePersonalData({
        data: {
          phone: {
            countryCode: user.phone.countryCode,
            countryNumberCode:
              COUNTRY_LIST.find(country => country.code === user.phone.countryCode)?.phoneCode ||
              DEFAULT_COUNTRY.phoneCode,
            number: user.phone.number,
            smsOptIn: value,
          },
        },
      }),
    );
  }

  private getUserClient(user: UserModel) {
    return user.role.name === UserRolesTypes.SuperAdmin
      ? user.userClients?.find(user => user.isManagingClient)?.id
      : user.userClients?.at(0)?.id;
  }
}
